import { useState } from 'react';
import axios from 'axios';
import { Phone, ShieldCheck, Star, Timer } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { business } from '../../mock';
import PhotoUpload from './PhotoUpload';
import SuburbChecker from './SuburbChecker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_BG =
  'https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=1366&q=55';

export default function Hero({ onQuoteClick }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    business_name: '',
    message: '',
    agree: false,
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.message) {
      toast({ title: 'Please fill in name, phone, address and message', variant: 'destructive' });
      return;
    }
    if (!form.agree) {
      toast({ title: 'Please agree to be contacted', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        address: form.address,
        business_name: form.business_name || null,
        message: form.message,
        source: 'hero_form',
        agreed_terms: form.agree,
        photos: photos.map(({ storage_path, filename, content_type, size_bytes }) => ({ storage_path, filename, content_type, size_bytes })),
      });
      toast({ title: 'Quote request received!', description: "We'll be in touch shortly with your free no obligation quote." });
      setForm({ name: '', phone: '', email: '', address: '', business_name: '', message: '', agree: false });
      setPhotos([]);
    } catch {
      toast({ title: 'Something went wrong', description: 'Please try again or call us directly.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="home" className="relative min-h-[760px] flex items-center overflow-hidden bg-[#0A2E18]">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
      <div className="absolute inset-0 bg-gradient-to-r from-[#06230F]/97 via-[#0A2E18]/90 to-[#0A2E18]/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#06230F] via-transparent to-[#06230F]/40" />

      <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-24 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        {/* Left: Copy */}
        <div className="lg:col-span-7 text-white">
          <p className="hero-rise text-[#FBC94B] font-bold tracking-[0.18em] text-xs uppercase mb-4 [text-shadow:_0_1px_4px_rgb(0_0_0_/_55%)]" style={{ animationDelay: '40ms' }}>
            Vic Garden &amp; Lawn Maintenance &middot; Ballarat
          </p>
          <h1 className="hero-rise text-white font-extrabold leading-[1.05] mb-4 max-w-[16ch]" style={{ fontSize: 'clamp(2.25rem, 4.5vw, 4rem)', animationDelay: '120ms' }}>
            Victoria&rsquo;s most trusted lawn &amp; garden crew.
          </h1>
          {/* Legacy trust line — same operator since 2016, rebranded as VGLM in 2022 */}
          <p className="hero-rise inline-flex items-center gap-2 mb-6 text-white/90 text-sm md:text-[15px] font-semibold" style={{ animationDelay: '200ms' }} data-testid="hero-legacy-line">
            <span className="inline-block w-6 h-px bg-[#D98A00]" aria-hidden="true" />
            Same trusted local crew since 2022 &middot; Now VGLM
          </p>
          <p className="hero-rise text-white/85 text-lg md:text-xl leading-relaxed mb-6 max-w-2xl" style={{ animationDelay: '260ms' }}>
            Reliable lawn mowing, gardening, weed control, and acreage mowing for homes across Ballarat, plus commercial grounds care across Victoria.
          </p>

          {/* 48-hour quote guarantee */}
          <div className="hero-rise mb-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A2E18]/70 border border-[#FBC94B]/55 text-[#FBC94B] text-sm font-bold" style={{ animationDelay: '320ms' }} data-testid="hero-guarantee">
            <Timer className="w-4 h-4" aria-hidden="true" />
            Free quote within 48 hours.
          </div>
          <div className="hero-rise flex flex-col sm:flex-row gap-3 mb-10" style={{ animationDelay: '380ms' }}>
            <button onClick={onQuoteClick} className="btn-gold px-7 py-3.5 rounded-md text-base">
              Request Free Quote
            </button>
            <a href={`tel:${business.phone}`} className="px-7 py-3.5 border-2 border-white/80 text-white font-bold rounded-md hover:bg-white hover:text-[#0A2E18] transition-colors flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" />
              Call {business.phone}
            </a>
          </div>

          {/* Consolidated trust bar with live Google rating pill */}
          <div className="hero-rise flex flex-wrap items-center gap-2 max-w-3xl" style={{ animationDelay: '440ms' }} data-testid="hero-trust-bar">
            {/* Google rating pill (primary trust signal) */}
            <a
              href={business.googleUrl || 'https://www.google.com/search?q=Vic+Garden+and+Lawn+Maintenance+Ballarat'}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-google-rating"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#0A2E18] text-xs font-extrabold hover:bg-[#FAF8F2] transition-colors"
            >
              <Star className="w-3.5 h-3.5 fill-[#FBC94B] text-[#FBC94B]" aria-hidden="true" />
              {Number(business.googleRating).toFixed(1)} &middot; {business.googleReviewCount} Google Reviews
            </a>
            {[
              'Fully Insured',
              'NDIS',
              'DVA',
              'Police Check',
              "Working With Children's Check",
            ].map((label) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0A2E18]/70 border border-[#FBC94B]/45 text-[#FBC94B] text-xs font-bold"
              >
                <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
                {label}
              </span>
            ))}
          </div>

          {/* Quick suburb / postcode checker */}
          <div className="hero-rise mt-6 max-w-md" style={{ animationDelay: '520ms' }} data-testid="hero-suburb-checker">
            <SuburbChecker variant="dark" onQuoteClick={onQuoteClick} />
          </div>
        </div>

        {/* Right: Quote form — matches Contact page form */}
        <div className="lg:col-span-5">
          <div className="hero-rise relative bg-white rounded-lg shadow-2xl overflow-hidden" style={{ animationDelay: '300ms' }}>
            <div className="h-1 bg-gradient-to-r from-[#1B6E37] via-[#D98A00] to-[#B86F00]" />
            <div className="p-6 md:p-7">
              <p className="section-label mb-2">Free Quote</p>
              <h3 className="text-2xl md:text-3xl font-extrabold mb-5 leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                Get your free no obligation quote
              </h3>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">CONTACT NAME *</label>
                    <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B6E37] focus:ring-2 focus:ring-[#1B6E37]/20" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">PHONE *</label>
                    <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B6E37] focus:ring-2 focus:ring-[#1B6E37]/20" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">EMAIL</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B6E37] focus:ring-2 focus:ring-[#1B6E37]/20" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">BUSINESS NAME</label>
                  <input type="text" placeholder="Optional" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B6E37] focus:ring-2 focus:ring-[#1B6E37]/20" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">JOB ADDRESS *</label>
                  <input type="text" placeholder="Street, suburb, postcode" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B6E37] focus:ring-2 focus:ring-[#1B6E37]/20" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">HOW CAN WE HELP? *</label>
                  <textarea rows="4" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your lawn & garden needs..."
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B6E37] focus:ring-2 focus:ring-[#1B6E37]/20 resize-none" />
                </div>
                <PhotoUpload value={photos} onChange={setPhotos} variant="light" />
                <label className="flex items-start gap-2 text-sm text-[#212B20] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.agree}
                    onChange={(e) => setForm({ ...form, agree: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-[#1B6E37]"
                  />
                  <span>I agree to be contacted about my enquiry by phone, SMS or email.</span>
                </label>
                <button type="submit" disabled={submitting} className="btn-gold w-full py-3 rounded-md disabled:opacity-60">
                  {submitting ? 'Sending...' : 'Send Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
